import apiClient, { cachedRequest } from 'apiClient';
import { Club, Network } from './types';
import { AccountRoleType } from 'screens/BrokerPortal/screens/Registration/components/PersonalDetailsStep/types';
import { Nullable } from 'utils/types';

export interface Firm {
  id: number;
  fcaNumber: string;
  name: string;
}

interface AccountPayload {
  firstName: string;
  surname: string;
  phone: string;
  email: string;
  role: AccountRoleType;
}

export interface RegisterAccountToExistingFirmPayload extends AccountPayload {
  firmId: number;
}

export interface RegisterFirmWithManagerPayload extends AccountPayload {
  firmName: string;
  fcaNumber: string;
  firmEmail: string;
  firmPhone: string;
  firmType: string;
  club: Nullable<number>;
  network: Nullable<number>;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  city: string;
  county: string;
  postcode: string;
  hasPermissionToRegisterFirm: boolean;
}

export class BadRequestError extends Error {
  private data: any;

  constructor(message: string, data: object) {
    super(message);
    this.data = data;
  }

  getMessage() {
    return this.data.details ? this.data.details.join(', ') : null;
  }
}
export class ServerError extends Error {}

export class ExpiredTokenError extends Error {}

export class InvalidTokenError extends Error {}

const handleRegisterError = (error: any) => {
  if (error.response) {
    if (error.response.status === 400) {
      throw new BadRequestError('BadRequestError', error.response.data);
    }
    if (error.response.status === 500) {
      throw new ServerError(error.response.data);
    }
  }

  throw error;
};

export const getClubsByClubName = async (clubName: string): Promise<Club[]> => {
  const response = await apiClient.get(`/api/v1/broker-management/search/club/${clubName}/`);
  return response.data;
};

export async function cachedGetClubsByClubName(clubName: string): Promise<Club[]> {
  const result = await cachedRequest<{ clubs?: Firm[]; error?: Error }>(async () => {
    try {
      const clubs = await getClubsByClubName(clubName);

      return { clubs };
    } catch (e) {
      return {
        error: e,
      };
    }
  }, `get-clubs-by-network-name-${clubName}`);

  if (result.error) {
    throw result.error;
  }

  if (!result.clubs) {
    throw new Error('Something went wrong while fetching clubs');
  }

  return result.clubs;
}

export const getNetworksByNetworkName = async (networkName: string): Promise<Network[]> => {
  const response = await apiClient.get(`/api/v1/broker-management/search/network/${networkName}/`);
  return response.data;
};

export async function cachedGetNetworksByNetworkName(networkName: string): Promise<Network[]> {
  const result = await cachedRequest<{ networks?: Firm[]; error?: Error }>(async () => {
    try {
      const networks = await getNetworksByNetworkName(networkName);

      return { networks };
    } catch (e) {
      return {
        error: e,
      };
    }
  }, `get-networks-by-network-name-${networkName}`);

  if (result.error) {
    throw result.error;
  }

  if (!result.networks) {
    throw new Error('Something went wrong while fetching networks');
  }

  return result.networks;
}

export const getFirmsByFCA = async (fca: string): Promise<Firm[]> => {
  const response = await apiClient.get(`/api/v1/broker-management/search/firm/${fca}/`);
  return response.data;
};

export async function cachedGetFirmsByFCA(fca: string): Promise<Firm[]> {
  const result = await cachedRequest<{ firms?: Firm[]; error?: Error }>(async () => {
    try {
      const firms = await getFirmsByFCA(fca);

      return { firms };
    } catch (e) {
      return {
        error: e,
      };
    }
  }, `get-firms-by-fca-${fca}`);

  if (result.error) {
    throw result.error;
  }

  if (!result.firms) {
    throw new Error('Something went wrong while fetching firms');
  }

  return result.firms;
}

export const getIsEmailAlreadyInUse = async (email: string): Promise<{ isExists: boolean }> => {
  const response = await apiClient.get(`/api/v1/broker-management/check-email/${email}`);
  return response.data;
};

export async function cachedGetIsEmailAlreadyInUse(email: string): Promise<{ isExists: boolean }> {
  const result = await cachedRequest<{ isExists: boolean; error?: Error }>(async () => {
    try {
      const { isExists } = await getIsEmailAlreadyInUse(email);

      return { isExists };
    } catch (e) {
      return {
        error: e,
      };
    }
  }, `get-is-email-already-in-use-${email}`);

  if (result.error) {
    throw result.error;
  }

  return result;
}

export const resendVerificationEmailWithToken = async (token: string): Promise<any> => {
  await apiClient.post(`/api/v1/broker-management/resend-verification-token/${token}`);
};

export const registerAccountToExistingFirm = async (data: RegisterAccountToExistingFirmPayload): Promise<any> => {
  try {
    const response = await apiClient.post(`/api/v1/broker-management/register/account/`, data);
    return response.data;
  } catch (error) {
    handleRegisterError(error);
  }
};

export const registerFirmWithManager = async (data: RegisterFirmWithManagerPayload): Promise<any> => {
  try {
    const response = await apiClient.post(`/api/v1/broker-management/register/firm-with-manager/`, data);
    return response.data;
  } catch (error) {
    handleRegisterError(error);
  }
};

export const verifyToken = async (token: string): Promise<any> => {
  try {
    const response = await apiClient.post(`/api/v1/broker-management/account/verify/${token}`);

    if (response.data.status === 'TOKEN_EXPIRED') {
      throw new ExpiredTokenError();
    }

    if (response.data.status === 'INVALID_TOKEN') {
      throw new InvalidTokenError();
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const setPassword = async (token: string, password: string): Promise<any> => {
  const response = await apiClient.post(`/api/v1/broker-management/account/set-password/${token}`, { password });
  return response.data;
};

export const getLatestTermsOfBusinessData = async (): Promise<{ url: string }> => {
  const response = await apiClient.get('/api/v1/documents/latest-term-of-business');
  return response.data;
};
